import { faTrashRestore } from '@fortawesome/free-solid-svg-icons'
import {
  faBookOpen,
  faScissors,
  faEnvelopeCircleCheck,
  faGlobe,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { SyntheticEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import DateFormats from '../../../../App/Helpers/DateFormats'
import { useAppDispatch } from '../../../../App/Hooks/reduxHooks'
import { useWindowDimensions } from '../../../../Common/Hooks/useWindowDimensions'
import { Icon } from '../../../../Common/Icon'
import { Spinner } from '../../../../Common/Spinner'
import { IWaitingCustomer } from '../../interface/IWaitingCustomer'
import {
  deleteWaitingCustomer,
  undoDeleteWaitingCustomer,
} from '../../store/Actions'
import { createActionButtonsHandler } from '../WaitingListCard/settings/actionButtonsHandler'

import {
  ActionButton,
  ActionWrapper,
  ActivityMenuIcon,
  ActivityMenuText,
  ActivityMenuWrapper,
  CardDescription,
  CardTitle,
  DeleteWrapper,
  WaitingCard,
} from './style'
import style from './style.scss'
import { set } from 'store'

type Props = {
  title: string
  subTitle?: string
  description?: string
  text?: string
  waitingCustomer: IWaitingCustomer
}

const StatusIconWrapper = styled.div`
  position: absolute;
  top: -12px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  font-size: 1.5rem;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  width: 30px;
  cursor: pointer;
  background-color: #2164db;
  border-radius: 20px;
`

const DeleteRevertButton = styled.button`
  color: black;
`

export const Card: React.FC<Props> = ({
  title,
  subTitle,
  description,
  text,
  waitingCustomer,
}) => {
  const { t } = useTranslation()
  const { windowWidth } = useWindowDimensions()
  const dispatch = useAppDispatch()

  const [isDeleted, setIsDeleted] = useState<boolean>(waitingCustomer.isDeleted)
  const [isUndoing, setIsUndoing] = useState<boolean>(false)

  const onDeleteClick = () => {
    setIsDeleted(true)
    dispatch(deleteWaitingCustomer(waitingCustomer))
  }

  let calendarUrl = `/calendar`
  const onActionButtonClick = (
    e: SyntheticEvent<HTMLButtonElement>,
    action: any,
  ) => {
    if (action.name === 'View customer') {
      window.location.replace(`/customers/${waitingCustomer.customer.id}`)
    } else if (action.name === 'Add to calendar') {
      if (waitingCustomer.wantedAt) {
        calendarUrl += `/${waitingCustomer.wantedAt.format(
          DateFormats.DATE_FORMAT_API,
        )}`
      }
      calendarUrl += `?customer_id=${waitingCustomer.customer.id}&waiting_customer_id=${waitingCustomer.id}`

      window.location.replace(calendarUrl)
    } else if (action.name === 'Delete') {
      onDeleteClick()
    }
    e.currentTarget.blur()
  }

  const actionButtonsHandler = createActionButtonsHandler(
    waitingCustomer.customer.id,
    calendarUrl,
    onDeleteClick,
  )

  return (
    <WaitingCard windowWidth={windowWidth}>
      <StatusIconWrapper>
        {waitingCustomer.isOnline && (
          <IconContainer title={t('Booked online')}>
            <Icon color={'white'} fontSize={18} icon={faGlobe} />
          </IconContainer>
        )}
      </StatusIconWrapper>
      <div className={style.content}>
        <CardTitle>{title}</CardTitle>
        {subTitle && <div className={style.subTitle}>{subTitle}</div>}
        {description && description.length > 0 ? (
          <CardDescription>{description}</CardDescription>
        ) : null}
        {text && text.length > 0 ? (
          <>
            <div className={style.text}>{text}</div>
          </>
        ) : null}
      </div>
      {waitingCustomer.lastMailSent !== undefined ? (
        <FontAwesomeIcon icon={faEnvelopeCircleCheck} />
      ) : null}
      {waitingCustomer &&
        waitingCustomer.matchingActivities &&
        waitingCustomer.matchingActivities.length > 0 && (
          <ActivityMenuWrapper>
            <ActivityMenuIcon
              icon={waitingCustomer.menuId ? faBookOpen : faScissors}
            />
            <div>
              {waitingCustomer.matchingActivities.map((activity, index) => (
                <ActivityMenuText key={index} windowWidth={windowWidth}>
                  {activity.name}
                </ActivityMenuText>
              ))}
            </div>
          </ActivityMenuWrapper>
        )}
      <ActionWrapper>
        {actionButtonsHandler.map((action, index) => {
          return (
            <ActionButton
              onClick={e => {
                onActionButtonClick(e, action)
              }}
              key={`cardAction${index}`}>
              <FontAwesomeIcon icon={action.icon} />
            </ActionButton>
          )
        })}
      </ActionWrapper>
      {isDeleted ? (
        <DeleteWrapper>
          {t('This customer has been removed from the waiting list.')}
          <DeleteRevertButton
            type={'button'}
            onClick={() => {
              setIsUndoing(true)
              dispatch(undoDeleteWaitingCustomer(waitingCustomer)).then(() => {
                setIsDeleted(false)
                setIsUndoing(false)
              })
            }}>
            <FontAwesomeIcon icon={faTrashRestore} /> {t('Undo')}
          </DeleteRevertButton>
        </DeleteWrapper>
      ) : isUndoing ? (
        <Spinner />
      ) : null}
    </WaitingCard>
  )
}
